import { useEffect } from 'react';

import { useCpaStore, useInitializedFlags } from '@liscio/common';

import { getSavedApplicationCpa, normalizeCompanyHost } from './cpa-utils';
import { useCpaV3 } from './useCpaV3';
import { useCpaV5 } from './useCpaV5';
import { useHomeData } from 'fetch-utils/users/user-hooks';

const useCpaDefaultValues: ReturnType<typeof useCpaV5> = {
  cpa: undefined,
  cpas: [],
  error: undefined,
  isLoading: true,
  selectCpa: () => Promise.resolve(),
  switchCpa: () => Promise.resolve(),
};

const getUseCpaValues = ({
  isFFInitialized,
  v5Login,
  useCpaV3Values,
  useCpaV5Values,
}: {
  isFFInitialized: boolean;
  v5Login: boolean;
  useCpaV5Values: ReturnType<typeof useCpaV5>;
  useCpaV3Values: ReturnType<typeof useCpaV3>;
}) => {
  if (!isFFInitialized) {
    return useCpaDefaultValues;
  }
  if (v5Login) {
    return useCpaV5Values;
  }
  return useCpaV3Values;
};

export const useCpa = () => {
  const {
    isInitialized: isFFInitialized,
    flags: { v5Login },
  } = useInitializedFlags();
  const {
    isLoading,
    setIsLoading,
    isInitialized,
    setIsInitialized,
    sessionCpa,
    appCpa,
    queryCompany,
    setQueryCompany,
    queryCpaUuid,
    setQueryCpaUuid,
    setSessionCpa,
  } = useCpaStore();
  const { data: homeData } = useHomeData();

  //useCpaV5
  const useCpaV5Values = useCpaV5();
  //useCpaV3
  const useCpaV3Values = useCpaV3();
  //useCpaValues
  const {
    cpa,
    cpas,
    error,
    isLoading: isHookLoading,
    selectCpa,
    switchCpa,
  } = getUseCpaValues({
    isFFInitialized,
    v5Login,
    useCpaV3Values,
    useCpaV5Values,
  });

  // Handle loading session cpa from homeData
  useEffect(() => {
    const userSessionData = homeData?.data;
    if (
      userSessionData &&
      userSessionData.cpa_id &&
      userSessionData.company_url &&
      userSessionData.cpa_id !== sessionCpa?.id
    ) {
      setSessionCpa({
        id: userSessionData.cpa_id,
        name: userSessionData.cpa_name,
        custom_liscio_url: userSessionData.company_url,
        logo: userSessionData.cpa_logo,
        cpa_uuid: userSessionData.cpa_uuid,
        is_current_cpa: true,
      });
    }
  }, [sessionCpa, setSessionCpa, homeData?.data]);

  // App CPA initialization
  useEffect(() => {
    // NOTE: This only gets called when already authenticated
    const initCpa = async () => {
      // Set loading state to avoid multiple executions
      setIsLoading(true);

      try {
        // autoset cpa when only 1 available. A firm override param is ignored
        // here deliberately: a single-firm user can only ever see their own
        // firm's data, so honoring a stray mismatched param would add friction
        // (cpa-selection with one entry) without protecting anything.
        if (cpas.length === 1 && !appCpa) {
          await selectCpa(cpas[0]);
        } else if (cpas.length > 1) {
          if (queryCpaUuid || queryCompany) {
            // A firm override (legacy-app redirects + the SmartVault Mirror
            // embed) names the firm this view MUST render — it is
            // authoritative, never a hint. Keys cascade strongest-first:
            // cpa_uuid (the exact key the switch mutation uses) wins when it
            // matches; a stale/unknown uuid degrades to the normalized
            // company_name host match rather than bouncing to cpa-selection.
            // Both keys only ever select from the user's own cpas, and there
            // is NO fallback to the saved cpa: when neither key matches, we
            // leave the cpa unselected so CpaRoute routes to cpa-selection.
            // Silently showing a different firm's data is the failure mode
            // this guards against (LISD-2544).
            const requestedHost = normalizeCompanyHost(queryCompany);
            const byUuid = queryCpaUuid
              ? cpas.find((cpa) => cpa.cpa_uuid === queryCpaUuid)
              : undefined;
            const byHost = requestedHost
              ? cpas.find(
                  (cpa) =>
                    normalizeCompanyHost(cpa.custom_liscio_url) ===
                    requestedHost
                )
              : undefined;
            const requestedCpa = byUuid ?? byHost;
            if (requestedCpa) {
              await selectCpa(requestedCpa);
            }
          } else {
            // No override — restore the cpa from storage when it's still one
            // of the user's cpas. The saved blob describes ONE firm, written
            // atomically by selectCpa: when it carries cpa_uuid (anything the
            // V5 path saved) the uuid alone decides — a stale uuid means a
            // stale blob, not a license to guess by host. Normalized host
            // matching only serves legacy/V3 blobs that lack the uuid (raw
            // url equality would drop those on a scheme/slash/case reformat).
            const savedCpa = await getSavedApplicationCpa();
            const savedHost = normalizeCompanyHost(
              savedCpa?.custom_liscio_url
            );
            const validatedCpa = savedCpa
              ? cpas.find((cpa) =>
                  savedCpa.cpa_uuid
                    ? cpa.cpa_uuid === savedCpa.cpa_uuid
                    : Boolean(savedHost) &&
                      normalizeCompanyHost(cpa.custom_liscio_url) === savedHost
                )
              : undefined;
            if (validatedCpa) {
              await selectCpa(validatedCpa);
            }
          }
        }
      } catch (error) {
        // A failed backend switch must not strand the app on the previous
        // firm (the old fire-and-forget selectCpa swallowed the rejection and
        // kept rendering it). appCpa stays unset, so CpaRoute redirects to
        // cpa-selection instead.
        // eslint-disable-next-line no-console
        console.error('[useCpa] cpa selection during init failed', error);
      } finally {
        // init complete
        setIsInitialized(true);
        setIsLoading(false);
        setQueryCompany(undefined);
        setQueryCpaUuid(undefined);
      }
    };

    if (!isInitialized && !isLoading && cpas.length && sessionCpa) {
      initCpa();
    }
  }, [
    cpas,
    sessionCpa,
    appCpa,
    selectCpa,
    queryCompany,
    setQueryCompany,
    queryCpaUuid,
    setQueryCpaUuid,
    isLoading,
    setIsLoading,
    isInitialized,
    setIsInitialized,
  ]);

  return {
    cpa,
    cpas,
    error,
    isLoading: isLoading || isHookLoading,
    selectCpa,
    switchCpa,
  };
};
